import { useState } from "react";
import styles from "./DynamicFormRenderer.module.css";

const DynamicFormRenderer = ({ section, data, onUpdate, onNext, onBack, isLastSection, isSubmitting, isSubmitted }) => {
    const [errors, setErrors] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);



    // Validate current section
    const validateSection = () => {
        const newErrors = {};

        section.questions.forEach(question => {
            if (question.required) {
                const value = data[question.id];

                if (!value || (Array.isArray(value) && value.length === 0) || value === '') {
                    newErrors[question.id] = 'This field is required';
                }

                // Number validation
                if (question.type === 'number' && value) {
                    if (question.validation) {
                        if (question.validation.min && value < question.validation.min) {
                            newErrors[question.id] = `Minimum value is ${question.validation.min}`;
                        }
                        if (question.validation.max && value > question.validation.max) {
                            newErrors[question.id] = `Maximum value is ${question.validation.max}`;
                        }
                    }
                }

                // Textarea max length
                if (question.type === 'textarea' && value && question.maxLength) {
                    if (value.length > question.maxLength) {
                        newErrors[question.id] = `Maximum ${question.maxLength} characters allowed`;
                    }
                }

                // Checkbox max selections
                if (question.type === 'checkbox' && value && question.maxSelections) {
                    if (value.length > question.maxSelections) {
                        newErrors[question.id] = `Maximum ${question.maxSelections} selections allowed`;
                    }
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateSection()) {
            if (isLastSection) {
                // Show confirmation modal for final submission
                setShowConfirmation(true);
            } else {
                // Just go to next section
                onNext();
            }
        }
    };

    const handleConfirmSubmit = () => {
        setShowConfirmation(false);
        onNext(); // Proceed with submission
    };

    const handleCancelSubmit = () => {
        setShowConfirmation(false);
    };

    const handleMultiSelect = (questionId, value, checked) => {
        if (isSubmitted) return;

        const current = data[questionId] || [];
        const updated = checked
            ? [...current, value]
            : current.filter(v => v !== value);
        onUpdate({ [questionId]: updated });
    };

    const renderQuestion = (question, index) => {
        const value = data[question.id];
        const error = errors[question.id];

        switch (question.type) {
            case 'text':
            case 'number':
                return (
                    <div key={question.id} className={styles.questionWrapper}>
                        <div className={styles.questionHeader}>
                            <span className={styles.questionNumber}>Q{index + 1}</span>
                            <label className={styles.questionLabel}>
                                {question.text}
                                {question.required && <span className={styles.required}>*</span>}
                            </label>
                        </div>
                        <div className={styles.inputWrapper}>
                            <input
                                type={question.type}
                                value={value || ''}
                                onChange={(e) => onUpdate({ [question.id]: question.type === 'number' ? parseInt(e.target.value) || '' : e.target.value })}
                                placeholder={question.placeholder || 'Enter your answer'}
                                min={question.validation?.min}
                                max={question.validation?.max}
                                disabled={isSubmitted}
                                className={`${styles.input} ${error ? styles.inputError : ''} ${isSubmitted ? styles.disabled : ''}`}
                            />
                            {error && (
                                <div className={styles.errorMessage}>
                                    <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    {error}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'textarea':
                return (
                    <div key={question.id} className={styles.questionWrapper}>
                        <div className={styles.questionHeader}>
                            <span className={styles.questionNumber}>Q{index + 1}</span>
                            <label className={styles.questionLabel}>
                                {question.text}
                                {question.required && <span className={styles.required}>*</span>}
                            </label>
                        </div>
                        <div className={styles.inputWrapper}>
                            <textarea
                                value={value || ''}
                                onChange={(e) => onUpdate({ [question.id]: e.target.value })}
                                placeholder={question.placeholder || 'Enter your detailed answer'}
                                maxLength={question.maxLength}
                                rows={5}
                                disabled={isSubmitted}
                                className={`${styles.textarea} ${error ? styles.inputError : ''} ${isSubmitted ? styles.disabled : ''}`}
                            />
                            {question.maxLength && (
                                <div className={styles.charCount}>
                                    <span className={value && value.length > question.maxLength * 0.8 ? styles.charCountWarning : ''}>
                                        {(value || '').length} / {question.maxLength} characters
                                    </span>
                                </div>
                            )}
                            {error && (
                                <div className={styles.errorMessage}>
                                    <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    {error}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'radio':
                if (!question.options || !Array.isArray(question.options)) {
                    console.error('Radio question missing options:', question);
                    return null;
                }

                return (
                    <div key={question.id} className={styles.questionWrapper}>
                        <div className={styles.questionHeader}>
                            <span className={styles.questionNumber}>Q{index + 1}</span>
                            <label className={styles.questionLabel}>
                                {question.text}
                                {question.required && <span className={styles.required}>*</span>}
                            </label>
                        </div>
                        <div className={styles.optionsWrapper}>
                            {question.options.map((option) => (
                                <label
                                    key={option}
                                    className={`${styles.radioOption} ${value === option ? styles.radioOptionSelected : ''} ${isSubmitted ? styles.disabled : ''}`}
                                    htmlFor={`${question.id}-${option}`}
                                >
                                    <input
                                        type="radio"
                                        name={question.id}
                                        value={option}
                                        id={`${question.id}-${option}`}
                                        checked={value === option}
                                        onChange={(e) => onUpdate({ [question.id]: e.target.value })}
                                        disabled={isSubmitted}
                                        className={styles.radioInput}
                                    />
                                    <span className={styles.radioCircle}>
                                        {value === option && <span className={styles.radioCircleInner}></span>}
                                    </span>
                                    <span className={styles.optionText}>{option}</span>
                                </label>
                            ))}
                        </div>
                        {question.hasOtherOption && value === 'Other (Please specify)' && (
                            <div className={styles.otherInputWrapper}>
                                <input
                                    type="text"
                                    value={data[`${question.id}_other`] || ''}
                                    onChange={(e) => onUpdate({ [`${question.id}_other`]: e.target.value })}
                                    placeholder="Please specify"
                                    disabled={isSubmitted}
                                    className={`${styles.input} ${isSubmitted ? styles.disabled : ''}`}
                                />
                            </div>
                        )}
                        {error && (
                            <div className={styles.errorMessage}>
                                <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </div>
                        )}
                    </div>
                );

            case 'checkbox':
                if (!question.options || !Array.isArray(question.options)) {
                    console.error('Checkbox question missing options:', question);
                    return null;
                }

                const selectedValues = value || [];
                const maxSelections = question.maxSelections; // coming directly from JSON

                return (
                    <div key={question.id} className={styles.questionWrapper}>
                        <div className={styles.questionHeader}>
                            <span className={styles.questionNumber}>Q{index + 1}</span>
                            <label className={styles.questionLabel}>
                                {question.text}
                                {question.required && <span className={styles.required}>*</span>}
                                {maxSelections && (
                                    <span className={styles.helperText}>
                                        (Select up to {maxSelections})
                                    </span>
                                )}
                            </label>
                        </div>

                        <div className={styles.optionsWrapper}>
                            {question.options.map((option) => {
                                const isChecked = selectedValues.includes(option);
                                const isDisabled =
                                    isSubmitted ||
                                    (!isChecked && maxSelections && selectedValues.length >= maxSelections);

                                return (
                                    <label
                                        key={option}
                                        className={`${styles.checkboxOption} 
                                ${isChecked ? styles.checkboxOptionSelected : ''} 
                                ${isDisabled ? styles.disabled : ''}`}
                                        htmlFor={`${question.id}-${option}`}
                                    >
                                        <input
                                            type="checkbox"
                                            id={`${question.id}-${option}`}
                                            checked={isChecked}
                                            onChange={(e) => {
                                                if (
                                                    !isChecked &&
                                                    maxSelections &&
                                                    selectedValues.length >= maxSelections
                                                ) {
                                                    alert(`You can select a maximum of ${maxSelections} options.`);
                                                    return;
                                                }
                                                handleMultiSelect(question.id, option, e.target.checked);
                                            }}
                                            disabled={isDisabled}
                                            className={styles.checkboxInput}
                                        />

                                        <span className={styles.checkboxBox}>
                                            {isChecked && (
                                                <svg
                                                    className={styles.checkIcon}
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            )}
                                        </span>
                                        <span className={styles.optionText}>{option}</span>
                                    </label>
                                );
                            })}
                        </div>

                        {error && (
                            <div className={styles.errorMessage}>
                                <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                {error}
                            </div>
                        )}
                    </div>
                );
            case 'rating':
                const min = question.scale?.min || 1;
                const max = question.scale?.max || 5;

                return (
                    <div key={question.id} className={styles.questionWrapper}>
                        <div className={styles.questionHeader}>
                            <span className={styles.questionNumber}>Q{index + 1}</span>
                            <label className={styles.questionLabel}>
                                {question.text}
                                {question.required && <span className={styles.required}>*</span>}
                            </label>
                        </div>
                        <div className={styles.ratingWrapper}>
                            <div className={styles.ratingButtons}>
                                {Array.from({ length: max - min + 1 }, (_, i) => i + min).map((rating) => (
                                    <button
                                        key={rating}
                                        type="button"
                                        onClick={() => !isSubmitted && onUpdate({ [question.id]: rating })}
                                        disabled={isSubmitted}
                                        className={`${styles.ratingButton} ${value === rating ? styles.ratingButtonActive : ''} ${isSubmitted ? styles.disabled : ''}`}
                                    >
                                        {rating}
                                    </button>
                                ))}
                            </div>
                            {question.scale?.labels && question.scale.labels.length > 0 && (
                                <div className={styles.ratingLabels}>
                                    <span className={styles.ratingLabelStart}>{question.scale.labels[0]}</span>
                                    <span className={styles.ratingLabelEnd}>{question.scale.labels[question.scale.labels.length - 1]}</span>
                                </div>
                            )}
                        </div>
                        {error && (
                            <div className={styles.errorMessage}>
                                <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </div>
                        )}
                    </div>
                );

            default:
                console.warn('Unknown question type:', question.type);
                return null;
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.formCard}>
                {/* Section Header */}
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>{section.title}</h2>
                    {section.description && (
                        <p className={styles.sectionDescription}>{section.description}</p>
                    )}
                </div>

                {/* Questions */}
                <div className={styles.questionsContainer}>
                    {section.questions.map((question, index) => renderQuestion(question, index))}
                </div>

                {/* Navigation Buttons */}
                <div className={styles.navigationBar}>
                    <div className={styles.buttonGroup}>
                        {onBack && (
                            <button
                                onClick={onBack}
                                className={styles.backButton}
                                disabled={isSubmitting || isSubmitted}
                            >
                                <svg className={styles.buttonIcon} viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                </svg>
                                Back
                            </button>
                        )}
                        <button
                            onClick={handleNext}
                            disabled={isSubmitting || isSubmitted}
                            className={`${styles.nextButton} ${isLastSection ? styles.submitButton : ''}`}
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className={styles.spinner} viewBox="0 0 24 24">
                                        <circle className={styles.spinnerCircle} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    </svg>
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    {isLastSection ? 'Submit Survey' : 'Next'}
                                    <svg className={styles.buttonIcon} viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmation && (
                <div className={styles.modalOverlay} onClick={handleCancelSubmit}>
                    <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                        {/* Icon Circle */}
                        <div className={styles.modalIconWrapper}>
                            <div className={styles.modalIconCircle}>
                                <svg className={styles.warningIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                            </div>
                        </div>

                        {/* Content */}
                        <div className={styles.modalContent}>
                            <h3 className={styles.modalTitle}>Confirm Study Submission</h3>

                            <div className={styles.modalBody}>
                                <p className={styles.modalMessage}>
                                    You are about to submit this study. <strong>No further modifications or data entry will be allowed.</strong>
                                </p>
                                <div className={styles.modalWarningBox}>
                                    <svg className={styles.infoIcon} viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                                    </svg>
                                    <span>Please review all information carefully before proceeding.</span>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className={styles.modalFooter}>
                                <button
                                    onClick={handleCancelSubmit}
                                    className={styles.cancelButton}
                                    type="button"
                                >
                                    <svg className={styles.buttonIconLeft} viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmSubmit}
                                    className={styles.confirmButton}
                                    type="button"
                                >
                                    <svg className={styles.buttonIconLeft} viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                    </svg>
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DynamicFormRenderer;